import { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { Modal } from '../../Modal';
import { useOnOutsidePopoverMouseDown } from '../../Popover/__internal__/hooks/useOnOutsidePopoverMouseDown';
import cn from './FloatingPanel.css.json';
import './FloatingPanel.css';

/**
 * Panel used in coordination with an ObjectSwitcher
 * @see https://rndconfluence.sas.com/confluence/pages/viewpage.action?spaceKey=NOVA&title=ObjectManager
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const FloatingPanel = props => {
  const {
    onOpenComplete,
    onCloseComplete
  } = props;
  const [panel, setPanel] = useState(null);
  const [{
    closing
  }, setAnimation] = useState({
    elementAcceptsAnimation: false,
    closing: false
  });
  const closingReason = useRef(null);
  const dt = useDesignTokens();
  const cssVars = renderInlineCssVars({
    sh: dt.tokens['shadow-depth-xl'],
    width: dt.tokens['size-container-l'],
    bg: dt.tokens['color-container-background-0'],
    bd: dt.tokens['color-container-border-4'],
    'anim-dur-enter': dt.tokens['duration-450'],
    'anim-tf-enter': dt.tokens['ease-in'],
    'anim-dur-exit': dt.tokens['duration-400'],
    'anim-tf-exit': dt.tokens['ease-out']
  }, 'floating-panel');
  useOnOutsidePopoverMouseDown(() => {
    closingReason.current = 'blur';
    setAnimation(prev => ({
      ...prev,
      closing: true
    }));
  }, panel);
  const onClose = useCallback(() => {
    setAnimation(prev => ({
      ...prev,
      closing: false
    }));
    onCloseComplete?.({
      closingReason: closingReason.current
    });
  }, [onCloseComplete]);

  // After the initial render call the onOpenComplete callback.
  useEffect(() => {
    if (panel) {
      onOpenComplete?.();
    }
  }, [onOpenComplete, panel]);

  // If closing is true, run the onClose event
  useEffect(() => {
    if (closing) {
      onClose();
    }
  }, [closing, onClose]);
  const animationEndHandler = useCallback(event => {
    if (event.target === event.currentTarget) {
      if (closing) {
        onClose();
      } else {
        setAnimation(prev => ({
          ...prev,
          elementAcceptsAnimation: true
        }));
      }
    }
  }, [closing, onClose]);
  useEffect(() => {
    const panelElement = panel;
    if (panelElement) {
      panelElement.addEventListener('animationend', animationEndHandler);
      if (props.isOpen) {
        panelElement.focus();
      }
      return () => {
        panelElement.removeEventListener('animationend', animationEndHandler);
      };
    }
  }, [animationEndHandler, panel, props.isOpen]);
  return /*#__PURE__*/_jsx(Modal, {
    isOpen: props.isOpen,
    children: /*#__PURE__*/_jsx("div", {
      ref: setPanel,
      role: props.role,
      tabIndex: props.tabIndex ?? -1,
      "aria-labelledby": props.ariaLabelledBy ? props.ariaLabelledBy : undefined,
      className: cx(cn.container, closing && cn.containerClosing),
      style: cssVars,
      "data-testid": props.testId,
      onAnimationEnd: animationEndHandler,
      onKeyDownCapture: ({
        key
      }) => {
        if (key === 'Escape') {
          closingReason.current = 'escape';
          setAnimation(prev => ({
            ...prev,
            closing: true
          }));
        }
      },
      children: props.children
    })
  });
};
//# sourceMappingURL=FloatingPanel.js.map